import {
  ModelFamily,
  ModelRouteAdapter,
  ModelSurface,
  ModelTopology,
  ReferenceTransportStrategy,
} from './schema';
import { IMAGE_ROLE_VALUES } from '../imageRoles.js';
import { MIDJOURNEY_MODEL_DEFINITION } from '../../services/midjourney/modelDefinition.js';
import { GLOWGHOST_ENGINE_MODEL_DEFINITIONS } from '../../services/glowghostEngine/modelDefinitions.js';
import {
  GROK_IMAGINE_ENDPOINTS,
  KLING_O1_IMAGE_ENDPOINTS,
  NANO_BANANA_2_ENDPOINTS,
  SEEDREAM_4_5_ENDPOINTS,
  SEEDREAM_5_PRO_ENDPOINTS,
} from './falEndpoints.js';
import { SCENE_ASSET_REFERENCE_BUCKET_KINDS } from '../../types/scene-assets/sceneAssetKinds';

const IMAGE_ASPECT_RATIOS = ['1:1', '3:4', '4:3', '2:3', '3:2', '9:16', '16:9'];
const NANO_LITE_ASPECT_RATIOS = [
  'auto',
  '21:9',
  '16:9',
  '3:2',
  '4:3',
  '5:4',
  '1:1',
  '4:5',
  '3:4',
  '2:3',
  '9:16',
  '4:1',
  '1:4',
  '8:1',
  '1:8',
];
const STANDARD_IMAGE_BATCH = Object.freeze({ min: 1, max: 4 });
const IMAGE_REF_MEDIA = ['image'];
const IMAGE_REF_ROLES = IMAGE_ROLE_VALUES;
const DEFAULT_CHECKED_AT = '2026-04-25';

const noReferences = {
  acceptedKinds: [],
  acceptedMediaTypes: [],
  roles: [],
  chips: [],
  limits: { total: 0 },
  transport: { strategy: ReferenceTransportStrategy.NONE },
  unsupportedBehavior: 'text-only',
};

const flatImageReferences = ({ total }) => ({
  acceptedKinds: SCENE_ASSET_REFERENCE_BUCKET_KINDS,
  acceptedMediaTypes: IMAGE_REF_MEDIA,
  roles: IMAGE_REF_ROLES,
  chips: IMAGE_REF_ROLES,
  limits: {
    total,
    character: total,
    prop: total,
    brand: total,
    environment: total,
    structure: total,
    style: total,
  },
  transport: {
    strategy: ReferenceTransportStrategy.FLAT,
    fields: { images: 'imageUrls' },
    buckets: {
      character: { max: total, order: 0 },
      prop: { max: total, order: 1 },
      brand: { max: total, order: 1.5 },
      environment: { max: total, order: 2 },
      structure: { max: total, order: 2.5 },
      style: { max: total, order: 3 },
    },
  },
  unsupportedBehavior: 'text-only',
});

// Gemini 3 Pro Image category limits:
// https://ai.google.dev/gemini-api/docs/image-generation
const nanoProReferences = {
  acceptedKinds: SCENE_ASSET_REFERENCE_BUCKET_KINDS,
  acceptedMediaTypes: IMAGE_REF_MEDIA,
  roles: IMAGE_REF_ROLES,
  chips: IMAGE_REF_ROLES,
  limits: {
    total: 14,
    character: 5,
    prop: 6,
    brand: 6,
    environment: 6,
    structure: 6,
    style: 3,
  },
  transport: {
    strategy: ReferenceTransportStrategy.ORDERED_BUCKETS,
    fields: { images: 'imageUrls' },
    buckets: {
      character: { max: 5, order: 0 },
      prop: { max: 6, order: 1 },
      brand: { max: 6, order: 1.5 },
      environment: { max: 6, order: 2 },
      structure: { max: 6, order: 2.5 },
      style: { max: 3, order: 3 },
    },
  },
  unsupportedBehavior: 'warn',
};

// The FAL `google/nano-banana-lite/edit` route maps to Gemini 3.1 Flash Lite,
// whose published image-input contract has a flat 14-image total and no
// per-kind categories: https://ai.google.dev/gemini-api/docs/image-generation
const nanoFlashReferences = {
  acceptedKinds: SCENE_ASSET_REFERENCE_BUCKET_KINDS,
  acceptedMediaTypes: IMAGE_REF_MEDIA,
  roles: IMAGE_REF_ROLES,
  chips: IMAGE_REF_ROLES,
  limits: {
    total: 14,
    character: 14,
    prop: 14,
    brand: 14,
    environment: 14,
    structure: 14,
    style: 14,
  },
  transport: {
    strategy: ReferenceTransportStrategy.ORDERED_BUCKETS,
    fields: { images: 'imageUrls' },
    buckets: {
      character: { max: 14, order: 0 },
      prop: { max: 14, order: 1 },
      brand: { max: 14, order: 1.5 },
      environment: { max: 14, order: 2 },
      structure: { max: 14, order: 2.5 },
      style: { max: 14, order: 3 },
    },
  },
  unsupportedBehavior: 'warn',
};

const klingO1References = {
  acceptedKinds: SCENE_ASSET_REFERENCE_BUCKET_KINDS,
  acceptedMediaTypes: IMAGE_REF_MEDIA,
  roles: IMAGE_REF_ROLES,
  chips: IMAGE_REF_ROLES,
  limits: {
    total: 10,
    character: 10,
    prop: 10,
    brand: 10,
    environment: 10,
    structure: 10,
    style: 10,
  },
  transport: {
    strategy: ReferenceTransportStrategy.PROMPT_TAGS,
    promptTagFormat: '@Image{index}',
    fields: { images: 'imageUrls' },
    buckets: {
      character: { max: 10, order: 0 },
      prop: { max: 10, order: 1 },
      brand: { max: 10, order: 1.5 },
      environment: { max: 10, order: 2 },
      structure: { max: 10, order: 2.5 },
      style: { max: 10, order: 3 },
    },
  },
  unsupportedBehavior: 'warn',
};

// Krea reads attached images as look references, never as a source image to
// edit, so its contract offers the style kind alone rather than the full
// multi-reference vocabulary.
const kreaStyleReferences = (total) => ({
  acceptedKinds: ['style'],
  acceptedMediaTypes: IMAGE_REF_MEDIA,
  roles: ['style'],
  chips: ['style'],
  limits: {
    total,
    style: total,
  },
  transport: {
    strategy: ReferenceTransportStrategy.FLAT,
    fields: { images: 'imageUrls' },
    buckets: {
      style: { max: total, order: 0 },
    },
  },
  unsupportedBehavior: 'text-only',
});

const ideogramCharacterReferences = {
  acceptedKinds: ['character'],
  acceptedMediaTypes: IMAGE_REF_MEDIA,
  roles: ['subject'],
  chips: ['subject'],
  limits: {
    total: 1,
    character: 1,
  },
  transport: {
    strategy: ReferenceTransportStrategy.SOURCE_SLOT,
    fields: { source: 'imageUrl' },
  },
  unsupportedBehavior: 'block',
};

function imageFeatures({
  aspectRatios = IMAGE_ASPECT_RATIOS,
  maxOutputImages = 4,
  maxInputGenerate = 0,
  maxInputEdit = 4,
  resolutions = null,
  defaultResolution = null,
  hasNegativePrompt = false,
  hasGuidanceScale = false,
  hasInferenceSteps = false,
  supportsBoardPresets = false,
  supportsReferenceNarration = false,
} = {}) {
  return {
    aspectRatio: aspectRatios ? { options: aspectRatios, default: aspectRatios[0] } : null,
    batch: { ...STANDARD_IMAGE_BATCH, max: maxOutputImages },
    promptImages: {
      generateMax: maxInputGenerate,
      editMax: maxInputEdit,
    },
    negativePrompt: { enabled: hasNegativePrompt },
    guidanceScale: { enabled: hasGuidanceScale },
    inferenceSteps: { enabled: hasInferenceSteps },
    resolution: resolutions
      ? { options: resolutions, default: defaultResolution || resolutions[0] }
      : null,
    boardPresets: { enabled: supportsBoardPresets },
    referenceNarration: { enabled: supportsReferenceNarration },
  };
}

function imageVerification(endpoint, checkedAt = DEFAULT_CHECKED_AT) {
  return {
    source: 'curated-existing-config',
    provider: 'fal',
    endpoint,
    checkedAt,
    unmappedProviderParams: [],
  };
}

function timing(baseSeconds, options = {}) {
  return {
    source: 'curated-existing-config',
    base: baseSeconds,
    baseSeconds,
    ...options,
  };
}

function imageRoute({
  endpoint,
  adapterId,
  serviceKey,
  exportName,
  features = {},
  references = null,
  timing: routeTiming = null,
}) {
  return {
    endpoint,
    features,
    references,
    timing: routeTiming,
    execution: {
      type: 'fal-service-export',
      adapterId,
      serviceKey,
      exportName,
      endpoint,
    },
  };
}

const BASE_IMAGE_MODEL_DEFINITIONS = [
  {
    id: 'depth-anything-v2',
    label: 'Depth Anything V2',
    family: ModelFamily.IMAGE,
    iconKey: 'generic-ai',
    pricing: { unit: 'image', usd: 0.01, asOf: '2026-05-31' },
    qualityTier: 'utility',
    timing: timing(4),
    surfaces: [ModelSurface.IMAGE_PREPROCESS],
    topologies: [ModelTopology.IMAGE_TO_DEPTH],
    features: {
      output: 'depth-map',
      generatedAssetType: 'image',
    },
    routes: {
      [ModelTopology.IMAGE_TO_DEPTH]: imageRoute({
        endpoint: 'fal-ai/image-preprocessors/depth-anything/v2',
        adapterId: ModelRouteAdapter.IMAGE_PREPROCESS_DEPTH,
        serviceKey: 'depthEstimation',
        exportName: 'generate',
        references: {
          ...noReferences,
          acceptedMediaTypes: ['image'],
          roles: ['source'],
          chips: ['source'],
          transport: {
            strategy: ReferenceTransportStrategy.SOURCE_SLOT,
            fields: { image: 'image_url' },
          },
        },
        timing: timing(4),
      }),
    },
    presentation: {
      label: 'Depth Anything V2',
      shortLabel: 'Depth Map',
      description: 'Generates monocular depth maps for depth-aware image effects.',
      group: 'Image preprocessing',
      tags: ['depth', 'preprocess', 'lens-blur'],
    },
    verification: imageVerification('fal-ai/image-preprocessors/depth-anything/v2'),
  },
  {
    id: 'grok-imagine',
    label: 'Grok Imagine Pro',
    family: ModelFamily.IMAGE,
    iconKey: 'grok',
    pricing: { unit: 'image', usd: { min: 0.04, max: 0.08 }, asOf: '2026-06-21' },
    qualityTier: 'stylized',
    timing: timing(20, { batchExtraMult: 0.35 }),
    surfaces: [ModelSurface.IMAGE_GENERATE, ModelSurface.IMAGE_EDIT],
    topologies: [ModelTopology.TEXT_TO_IMAGE, ModelTopology.IMAGE_TO_IMAGE],
    features: imageFeatures({
      maxInputEdit: 3,
      resolutions: ['1K', '2K'],
      defaultResolution: '1K',
    }),
    references: flatImageReferences({ total: 3 }),
    routes: {
      [ModelTopology.TEXT_TO_IMAGE]: imageRoute({
        endpoint: GROK_IMAGINE_ENDPOINTS.textToImage,
        adapterId: 'image.text.resolution',
        serviceKey: 'grokImagine',
        exportName: 'generateGrokImagine',
        references: noReferences,
        timing: timing(20, { batchExtraMult: 0.35 }),
      }),
      [ModelTopology.IMAGE_TO_IMAGE]: imageRoute({
        endpoint: GROK_IMAGINE_ENDPOINTS.edit,
        adapterId: 'image.edit.flat',
        serviceKey: 'grokImagineEdit',
        exportName: 'generateGrokImagineEdit',
        timing: timing(28, { batchExtraMult: 0.35, inputImageExtraMult: 0.08 }),
      }),
    },
    prompt: { enhancerId: 'grok-imagine' },
    agent: { exposeAs: ['image-generator'] },
    ui: {
      image: {
        editKey: 'grok-imagine-edit',
        attachedImageMode: 'edit',
        creativeAdapter: 'image-text-basic',
      },
    },
    verification: imageVerification(GROK_IMAGINE_ENDPOINTS.textToImage),
  },
  {
    id: 'flux-2-pro',
    label: 'Flux 2 Pro',
    family: ModelFamily.IMAGE,
    iconKey: 'flux',
    pricing: { unit: 'megapixel', usd: 0.03, asOf: '2026-04-25' },
    qualityTier: 'standard',
    timing: timing(12),
    surfaces: [ModelSurface.IMAGE_GENERATE, ModelSurface.IMAGE_EDIT],
    topologies: [ModelTopology.TEXT_TO_IMAGE, ModelTopology.IMAGE_TO_IMAGE],
    features: imageFeatures({
      maxOutputImages: 1,
      maxInputEdit: 8,
      resolutions: ['1K', '2K'],
      defaultResolution: '1K',
    }),
    references: flatImageReferences({ total: 8 }),
    routes: {
      [ModelTopology.TEXT_TO_IMAGE]: imageRoute({
        endpoint: 'fal-ai/flux-2-pro',
        adapterId: 'image.text.dimensions',
        serviceKey: 'flux2Pro',
        exportName: 'generateFlux2Pro',
        references: noReferences,
        timing: timing(12),
      }),
      [ModelTopology.IMAGE_TO_IMAGE]: imageRoute({
        endpoint: 'fal-ai/flux-2-pro/edit',
        adapterId: 'image.edit.dimensions',
        serviceKey: 'flux2Edit',
        exportName: 'generateFlux2Edit',
        timing: timing(24, { inputImageExtraMult: 0.08 }),
      }),
    },
    prompt: { enhancerId: 'flux-2-pro', editEnhancerId: 'flux-2-edit' },
    agent: { exposeAs: ['image-generator'] },
    ui: {
      generativeFill: {
        variant: 'smart',
        order: 1,
        tierLabel: 'Balanced',
        label: 'FLUX 2 Pro Edit',
        enhancementKey: 'flux-2-edit',
        serviceKey: 'flux2Edit',
        imageInputMode: 'imageUrls',
        loadingModelKey: 'flux-2-edit',
        defaultParams: {
          imageSize: 'auto',
          outputFormat: 'png',
        },
      },
      image: {
        editKey: 'flux-2-edit',
        attachedImageMode: 'edit',
        creativeAdapter: 'image-text-basic',
      },
    },
    verification: imageVerification('fal-ai/flux-2-pro'),
  },
  {
    id: 'seedream-4.5',
    label: 'Seedream 4.5',
    series: { id: 'seedream', version: '4.5', rank: 1 },
    family: ModelFamily.IMAGE,
    iconKey: 'bytedance',
    pricing: { unit: 'image', usd: 0.04, asOf: '2026-04-25' },
    qualityTier: 'design',
    timing: timing(20, { batchExtraMult: 0.35 }),
    surfaces: [ModelSurface.IMAGE_GENERATE, ModelSurface.IMAGE_EDIT],
    topologies: [ModelTopology.TEXT_TO_IMAGE, ModelTopology.IMAGE_TO_IMAGE],
    features: imageFeatures({ maxInputEdit: 10, supportsReferenceNarration: true }),
    references: flatImageReferences({ total: 10 }),
    routes: {
      [ModelTopology.TEXT_TO_IMAGE]: imageRoute({
        endpoint: SEEDREAM_4_5_ENDPOINTS.textToImage,
        adapterId: 'image.text.basic',
        serviceKey: 'seedream45',
        exportName: 'generateSeedream45',
        references: noReferences,
        timing: timing(20, { batchExtraMult: 0.35 }),
      }),
      [ModelTopology.IMAGE_TO_IMAGE]: imageRoute({
        endpoint: SEEDREAM_4_5_ENDPOINTS.edit,
        adapterId: 'image.edit.flat',
        serviceKey: 'seedream45',
        exportName: 'generateSeedream45Edit',
        timing: timing(30, { batchExtraMult: 0.35, inputImageExtraMult: 0.06 }),
      }),
    },
    prompt: { enhancerId: 'seedream-4.5' },
    agent: { exposeAs: ['image-generator'] },
    activation: { defaultActive: false },
    ui: {
      image: {
        editKey: 'seedream-4.5-edit',
        attachedImageMode: 'edit',
        creativeAdapter: 'image-text-basic',
      },
    },
    verification: imageVerification(SEEDREAM_4_5_ENDPOINTS.textToImage),
  },
  {
    id: 'seedream-5-pro',
    label: 'Seedream 5.0 Pro',
    series: { id: 'seedream', version: '5.0 Pro', rank: 2 },
    family: ModelFamily.IMAGE,
    iconKey: 'bytedance',
    pricing: { unit: 'image', usd: 0.035, asOf: '2026-04-25' },
    qualityTier: 'reasoning',
    timing: timing(24, { batchExtraMult: 0.45 }),
    surfaces: [ModelSurface.IMAGE_GENERATE, ModelSurface.IMAGE_EDIT],
    topologies: [ModelTopology.TEXT_TO_IMAGE, ModelTopology.IMAGE_TO_IMAGE],
    features: imageFeatures({ maxInputEdit: 10, supportsReferenceNarration: true }),
    references: flatImageReferences({ total: 10 }),
    routes: {
      [ModelTopology.TEXT_TO_IMAGE]: imageRoute({
        endpoint: SEEDREAM_5_PRO_ENDPOINTS.textToImage,
        adapterId: 'image.text.basic',
        serviceKey: 'seedream5Pro',
        exportName: 'generateSeedream5Pro',
        references: noReferences,
        timing: timing(24, { batchExtraMult: 0.45 }),
      }),
      [ModelTopology.IMAGE_TO_IMAGE]: imageRoute({
        endpoint: SEEDREAM_5_PRO_ENDPOINTS.edit,
        adapterId: 'image.edit.flat',
        serviceKey: 'seedream5Pro',
        exportName: 'generateSeedream5ProEdit',
        timing: timing(34, { batchExtraMult: 0.45, inputImageExtraMult: 0.06 }),
      }),
    },
    prompt: { enhancerId: 'seedream-5-pro' },
    agent: { exposeAs: ['image-generator'] },
    activation: { defaultActive: false },
    ui: {
      image: {
        editKey: 'seedream-5-pro-edit',
        attachedImageMode: 'edit',
        creativeAdapter: 'image-text-basic',
      },
    },
    verification: imageVerification(SEEDREAM_5_PRO_ENDPOINTS.textToImage),
  },
  {
    id: 'gpt-image-2.5',
    label: 'GPT Image 2.5 Flare',
    series: { id: 'gpt-image-2.5', version: 'Flare', rank: 2 },
    family: ModelFamily.IMAGE,
    iconKey: 'openai',
    pricing: { unit: 'image', usd: { min: 0.004, max: 0.4 }, asOf: '2026-09-09' },
    qualityTier: 'structured',
    timing: timing(60, {
      batchExtraMult: 0.55,
      boardPresetMult: 1.25,
      // xhigh/max calibrated from API pricing steps, not measured
      qualities: { low: 0.45, medium: 0.7, high: 1, xhigh: 1.5, max: 2.2 },
    }),
    surfaces: [ModelSurface.IMAGE_GENERATE, ModelSurface.IMAGE_EDIT],
    topologies: [ModelTopology.TEXT_TO_IMAGE, ModelTopology.IMAGE_TO_IMAGE],
    features: imageFeatures({ maxInputEdit: 16, supportsBoardPresets: true }),
    references: flatImageReferences({ total: 16 }),
    routes: {
      [ModelTopology.TEXT_TO_IMAGE]: imageRoute({
        endpoint: 'openai/gpt-image-2.5/flare/text-to-image',
        adapterId: 'image.text.basic',
        serviceKey: 'gptImage25',
        exportName: 'generateGptImage25',
        references: noReferences,
        timing: timing(60, {
          batchExtraMult: 0.55,
          boardPresetMult: 1.25,
          qualities: { low: 0.45, medium: 0.7, high: 1, xhigh: 1.5, max: 2.2 },
        }),
      }),
      [ModelTopology.IMAGE_TO_IMAGE]: imageRoute({
        endpoint: 'openai/gpt-image-2.5/flare/edit',
        adapterId: 'image.edit.flat',
        serviceKey: 'gptImage25',
        exportName: 'generateGptImage25Edit',
        timing: timing(75, {
          batchExtraMult: 0.55,
          boardPresetMult: 1.25,
          inputImageExtraMult: 0.08,
          qualities: { low: 0.45, medium: 0.7, high: 1, xhigh: 1.5, max: 2.2 },
        }),
      }),
    },
    prompt: { enhancerId: 'gpt-image-2.5' },
    agent: { exposeAs: ['image-generator'] },
    ui: {
      generativeFill: {
        variant: 'smart',
        order: 3,
        tierLabel: 'Precise',
        label: 'GPT Image 2.5 Flare',
        enhancementKey: 'generativeFill:smart:gptImage25',
        serviceKey: 'gptImage25',
        imageInputMode: 'imageUrls',
        loadingModelKey: 'gpt-image-2.5-edit',
        defaultParams: {
          aspectRatio: 'auto',
          quality: 'high',
          outputFormat: 'png',
        },
      },
      image: {
        editKey: 'gpt-image-2.5-edit',
        attachedImageMode: 'edit',
        creativeAdapter: 'image-text-basic',
      },
    },
    verification: imageVerification('openai/gpt-image-2.5/flare/text-to-image'),
  },
  {
    id: 'gpt-image-2.5-sunburst',
    label: 'GPT Image 2.5 Sunburst',
    series: { id: 'gpt-image-2.5', version: 'Sunburst', rank: 1 },
    family: ModelFamily.IMAGE,
    iconKey: 'openai',
    pricing: { unit: 'image', usd: { min: 0.004, max: 0.4 }, asOf: '2026-09-26' },
    qualityTier: 'precision',
    timing: timing(105, {
      batchExtraMult: 0.55,
      boardPresetMult: 1.25,
      qualities: { low: 0.45, medium: 0.7, high: 1, xhigh: 1.5, max: 2.2 },
    }),
    surfaces: [ModelSurface.IMAGE_GENERATE, ModelSurface.IMAGE_EDIT],
    topologies: [ModelTopology.TEXT_TO_IMAGE, ModelTopology.IMAGE_TO_IMAGE],
    features: imageFeatures({ maxInputEdit: 16, supportsBoardPresets: true }),
    references: flatImageReferences({ total: 16 }),
    routes: {
      [ModelTopology.TEXT_TO_IMAGE]: imageRoute({
        endpoint: 'openai/gpt-image-2.5/sunburst/text-to-image',
        adapterId: 'image.text.basic',
        serviceKey: 'gptImage25',
        exportName: 'generateGptImage25Sunburst',
        references: noReferences,
        timing: timing(105, {
          batchExtraMult: 0.55,
          boardPresetMult: 1.25,
          qualities: { low: 0.45, medium: 0.7, high: 1, xhigh: 1.5, max: 2.2 },
        }),
      }),
      [ModelTopology.IMAGE_TO_IMAGE]: imageRoute({
        endpoint: 'openai/gpt-image-2.5/sunburst/edit',
        adapterId: 'image.edit.flat',
        serviceKey: 'gptImage25',
        exportName: 'generateGptImage25SunburstEdit',
        timing: timing(125, {
          batchExtraMult: 0.55,
          boardPresetMult: 1.25,
          inputImageExtraMult: 0.08,
          qualities: { low: 0.45, medium: 0.7, high: 1, xhigh: 1.5, max: 2.2 },
        }),
      }),
    },
    prompt: { enhancerId: 'gpt-image-2.5-sunburst' },
    agent: { exposeAs: ['image-generator'] },
    ui: {
      image: {
        editKey: 'gpt-image-2.5-sunburst-edit',
        attachedImageMode: 'edit',
        creativeAdapter: 'image-text-basic',
      },
    },
    verification: imageVerification('openai/gpt-image-2.5/sunburst/text-to-image', '2026-09-25'),
  },
  {
    id: 'nano-banana-2-flash',
    label: 'Nano Banana 2 Flash',
    series: { id: 'nano-banana', version: '2 Flash', rank: 1 },
    family: ModelFamily.IMAGE,
    iconKey: 'gemini',
    pricing: { unit: 'image', usd: 0.034, asOf: '2026-07-07' },
    qualityTier: 'enhanced',
    timing: timing(4, {
      batchExtraMult: 0.25,
      thinkingLevels: { minimal: 1.05, high: 1.35 },
    }),
    surfaces: [ModelSurface.IMAGE_GENERATE, ModelSurface.IMAGE_EDIT],
    topologies: [ModelTopology.TEXT_TO_IMAGE, ModelTopology.IMAGE_TO_IMAGE],
    features: imageFeatures({
      aspectRatios: NANO_LITE_ASPECT_RATIOS,
      maxInputEdit: 14,
      resolutions: ['1K'],
      defaultResolution: '1K',
      supportsReferenceNarration: true,
    }),
    references: nanoFlashReferences,
    routes: {
      [ModelTopology.TEXT_TO_IMAGE]: imageRoute({
        endpoint: 'google/nano-banana-2-lite',
        adapterId: 'image.text.resolution',
        serviceKey: 'nanoBanana2Flash',
        exportName: 'generateNanoBanana2Flash',
        references: noReferences,
        timing: timing(4, {
          batchExtraMult: 0.25,
          thinkingLevels: { minimal: 1.05, high: 1.35 },
        }),
      }),
      [ModelTopology.IMAGE_TO_IMAGE]: imageRoute({
        endpoint: 'google/nano-banana-lite/edit',
        adapterId: 'image.edit.bucketed',
        serviceKey: 'nanoBanana2FlashEdit',
        exportName: 'generateNanoBanana2FlashEdit',
        timing: timing(8, {
          batchExtraMult: 0.25,
          inputImageExtraMult: 0.06,
          thinkingLevels: { minimal: 1.05, high: 1.35 },
        }),
      }),
    },
    prompt: { enhancerId: 'nano-banana-2' },
    agent: { exposeAs: ['image-generator'], defaultFor: ['image-generator'] },
    ui: {
      generativeFill: {
        variant: 'smart',
        order: 0,
        tierLabel: 'Fastest',
        label: 'Nano Banana 2 Flash',
        enhancementKey: 'generativeFill:smart:nanoBanana2Flash',
        serviceKey: 'nanoBanana2FlashEdit',
        imageInputMode: 'imageUrls',
        loadingModelKey: 'nano-banana-2-flash-edit',
        defaultParams: { resolution: '1K' },
      },
      image: {
        editKey: 'nano-banana-2-flash-edit',
        attachedImageMode: 'edit',
        creativeAdapter: 'image-text-basic',
        supportsCharacterReferences: true,
      },
    },
    verification: imageVerification('google/nano-banana-2-lite'),
  },
  {
    id: 'nano-banana-2',
    label: 'Nano Banana Pro',
    series: { id: 'nano-banana', version: 'Pro', rank: 2 },
    family: ModelFamily.IMAGE,
    iconKey: 'gemini',
    pricing: { unit: 'image', usd: 0.15, asOf: '2026-04-25' },
    qualityTier: 'premium',
    timing: timing(40, {
      batchExtraMult: 0.45,
      resolutions: { '1K': 0.75, '2K': 1, '4K': 1.8 },
      webSearchMult: 1.25,
    }),
    surfaces: [ModelSurface.IMAGE_GENERATE, ModelSurface.IMAGE_EDIT],
    topologies: [ModelTopology.TEXT_TO_IMAGE, ModelTopology.IMAGE_TO_IMAGE],
    features: imageFeatures({
      maxInputEdit: 14,
      resolutions: ['1K', '2K', '4K'],
      defaultResolution: '2K',
      // No negativePrompt: `fal-ai/nano-banana-pro` has no `negative_prompt` input and FAL
      // drops unknown fields without erroring, so declaring it turned every consumer's ban
      // into a no-op. Verified against the endpoint's own OpenAPI schema, not inferred.
      supportsReferenceNarration: true,
    }),
    references: nanoProReferences,
    routes: {
      [ModelTopology.TEXT_TO_IMAGE]: imageRoute({
        endpoint: NANO_BANANA_2_ENDPOINTS.textToImage,
        adapterId: 'image.text.resolution',
        serviceKey: 'nanoBanana2',
        exportName: 'generateNanoBanana2',
        references: noReferences,
        timing: timing(40, {
          batchExtraMult: 0.45,
          resolutions: { '1K': 0.75, '2K': 1, '4K': 1.8 },
          webSearchMult: 1.25,
        }),
      }),
      [ModelTopology.IMAGE_TO_IMAGE]: imageRoute({
        endpoint: NANO_BANANA_2_ENDPOINTS.edit,
        adapterId: 'image.edit.bucketed',
        serviceKey: 'nanoBanana2Edit',
        exportName: 'generateNanoBanana2Edit',
        timing: timing(58, {
          batchExtraMult: 0.45,
          inputImageExtraMult: 0.06,
          resolutions: { '1K': 0.75, '2K': 1, '4K': 1.7 },
          webSearchMult: 1.25,
        }),
      }),
    },
    prompt: { enhancerId: 'nano-banana-2' },
    agent: { exposeAs: ['image-generator'] },
    ui: {
      generativeFill: {
        variant: 'smart',
        order: 2,
        tierLabel: 'Consistent',
        label: 'Nano Banana Pro',
        loadingModelKey: 'nano-banana-2-edit',
        enhancementKey: 'generativeFill:smart:nanoBanana2',
        serviceKey: 'nanoBanana2Edit',
        imageInputMode: 'imageUrls',
        defaultParams: { resolution: '2K' },
      },
      image: {
        editKey: 'nano-banana-2-edit',
        attachedImageMode: 'edit',
        creativeAdapter: 'image-text-basic',
        supportsCharacterReferences: true,
      },
    },
    verification: imageVerification(NANO_BANANA_2_ENDPOINTS.textToImage),
  },
  {
    id: 'ideogram-character',
    label: 'Ideogram Character',
    family: ModelFamily.IMAGE,
    iconKey: 'ideogram',
    pricing: { unit: 'image', usd: 0.1, asOf: '2026-04-25' },
    qualityTier: 'character',
    // Direct model-key loading uses the character-reference estimate; remix route loading is slower.
    timing: timing(24, { batchExtraMult: 0.35 }),
    surfaces: [ModelSurface.IMAGE_GENERATE, ModelSurface.IMAGE_EDIT],
    topologies: [ModelTopology.IMAGE_TO_IMAGE],
    features: imageFeatures({
      maxOutputImages: 4,
      maxInputGenerate: 1,
      maxInputEdit: 2,
      hasNegativePrompt: true,
    }),
    references: ideogramCharacterReferences,
    routes: {
      [ModelTopology.IMAGE_TO_IMAGE]: imageRoute({
        endpoint: 'fal-ai/ideogram/character/remix',
        adapterId: 'image.reference.single-character',
        serviceKey: 'ideogramCharacter',
        exportName: 'generateIdeogramCharacterRemix',
        references: ideogramCharacterReferences,
        timing: timing(30, { batchExtraMult: 0.35, inputImageExtraMult: 0.08 }),
      }),
    },
    prompt: { enhancerId: 'ideogram-character' },
    agent: { exposeAs: ['image-generator'] },
    activation: { defaultActive: false },
    ui: {
      image: {
        editKey: 'ideogram-character-remix',
        attachedImageMode: 'reference',
        requiresPromptImages: true,
        creativeAdapter: 'image-reference-single-character',
        supportsCharacterReferences: true,
        maxOutputEdit: 1,
        generateExecution: {
          type: 'fal-service-export',
          adapterId: 'image.reference.single-character',
          serviceKey: 'ideogramCharacter',
          exportName: 'generateIdeogramCharacter',
          endpoint: 'fal-ai/ideogram/character',
        },
        editExecution: {
          type: 'fal-service-export',
          adapterId: 'image.reference.single-character-remix',
          serviceKey: 'ideogramCharacter',
          exportName: 'generateIdeogramCharacterRemix',
          endpoint: 'fal-ai/ideogram/character/remix',
        },
      },
    },
    verification: imageVerification('fal-ai/ideogram/character/remix'),
  },
  {
    id: 'kling-o1-image',
    label: 'Kling O1 Image',
    family: ModelFamily.IMAGE,
    iconKey: 'kling',
    pricing: { unit: 'image', usd: 0.028, asOf: '2026-04-25' },
    qualityTier: 'props',
    timing: timing(36, {
      batchExtraMult: 0.4,
      inputImageExtraMult: 0.06,
      resolutions: { '1K': 1, '2K': 1.35 },
    }),
    surfaces: [ModelSurface.IMAGE_GENERATE, ModelSurface.IMAGE_EDIT],
    topologies: [ModelTopology.TEXT_TO_IMAGE, ModelTopology.IMAGE_TO_IMAGE],
    features: imageFeatures({
      maxOutputImages: 9,
      maxInputGenerate: 10,
      maxInputEdit: 10,
      resolutions: ['1K', '2K'],
      defaultResolution: '1K',
      supportsReferenceNarration: true,
    }),
    references: klingO1References,
    routes: {
      [ModelTopology.TEXT_TO_IMAGE]: imageRoute({
        endpoint: KLING_O1_IMAGE_ENDPOINTS.generate,
        adapterId: 'image.reference.prompt-tags',
        serviceKey: 'klingO1Image',
        exportName: 'generateKlingO1Image',
        references: klingO1References,
        timing: timing(36, {
          batchExtraMult: 0.4,
          inputImageExtraMult: 0.06,
          resolutions: { '1K': 1, '2K': 1.35 },
        }),
      }),
      [ModelTopology.IMAGE_TO_IMAGE]: imageRoute({
        endpoint: KLING_O1_IMAGE_ENDPOINTS.generate,
        adapterId: 'image.reference.prompt-tags',
        serviceKey: 'klingO1Image',
        exportName: 'generateKlingO1Image',
        references: klingO1References,
        timing: timing(42, {
          batchExtraMult: 0.4,
          inputImageExtraMult: 0.06,
          resolutions: { '1K': 1, '2K': 1.35 },
        }),
      }),
    },
    prompt: { enhancerId: 'kling-o1-image' },
    agent: { exposeAs: ['image-generator'] },
    activation: { defaultActive: false },
    ui: {
      image: {
        editKey: 'kling-o1-image-edit',
        attachedImageMode: 'reference',
        requiresPromptImages: true,
        creativeAdapter: 'image-reference-positional',
        supportsProps: true,
      },
    },
    verification: imageVerification(KLING_O1_IMAGE_ENDPOINTS.generate),
  },
  {
    id: 'z-image-turbo-inpaint',
    label: 'Z-Image Turbo Inpaint',
    family: ModelFamily.IMAGE,
    iconKey: 'fal',
    qualityTier: 'fast',
    // fal list price: https://fal.ai/models/fal-ai/z-image/turbo/inpaint
    pricing: { unit: 'megapixel', usd: 0.01, asOf: '2026-10-20' },
    timing: timing(10),
    surfaces: [ModelSurface.IMAGE_INPAINT],
    topologies: [ModelTopology.MASK_IMAGE_TO_IMAGE],
    features: imageFeatures({ maxOutputImages: 4, maxInputEdit: 1 }),
    references: noReferences,
    routes: {
      [ModelTopology.MASK_IMAGE_TO_IMAGE]: imageRoute({
        endpoint: 'fal-ai/z-image/turbo/inpaint',
        adapterId: 'image.inpaint.mask',
        serviceKey: 'zImageTurboInpaint',
        exportName: 'generate',
        references: noReferences,
        timing: timing(10),
      }),
    },
    prompt: { enhancerId: 'generativeFill:precise:zImageTurbo' },
    // No agent tool reaches this capability, so an `exposeAs` here rendered a
    // Settings > Models agent-default row the agent could never honour. Manual
    // use is unaffected: `exposeAs` gates agent surfaces only. Re-add the role in
    // the same change that adds the tool.
    agent: { exposeAs: [] },
    ui: {
      generativeFill: {
        variant: 'precise',
        order: 0,
        tierLabel: 'Fastest',
        label: 'Z-Image Turbo',
        enhancementKey: 'generativeFill:precise:zImageTurbo',
        serviceKey: 'zImageTurboInpaint',
        imageInputMode: 'image',
        defaultParams: {
          acceleration: 'regular',
          controlScale: 0.75,
          controlStart: 0,
          controlEnd: 0.8,
          enablePromptExpansion: false,
          imageSize: 'auto',
          inferenceSteps: 8,
          outputFormat: 'png',
          strength: 1,
        },
      },
    },
    verification: imageVerification('fal-ai/z-image/turbo/inpaint'),
  },
  {
    id: 'flux-pro-fill',
    label: 'FLUX.1 Pro Fill',
    family: ModelFamily.IMAGE,
    iconKey: 'flux',
    qualityTier: 'standard',
    // fal list price: https://fal.ai/models/fal-ai/flux-pro/v1/fill
    pricing: { unit: 'megapixel', usd: 0.05, asOf: '2026-10-20' },
    timing: timing(32),
    surfaces: [ModelSurface.IMAGE_INPAINT],
    topologies: [ModelTopology.MASK_IMAGE_TO_IMAGE],
    features: imageFeatures({ maxOutputImages: 4, maxInputEdit: 1 }),
    references: noReferences,
    routes: {
      [ModelTopology.MASK_IMAGE_TO_IMAGE]: imageRoute({
        endpoint: 'fal-ai/flux-pro/v1/fill',
        adapterId: 'image.inpaint.mask',
        serviceKey: 'inpaint',
        exportName: 'generate',
        references: noReferences,
        timing: timing(32),
      }),
    },
    prompt: { enhancerId: 'generativeFill:precise:fluxFill' },
    // No agent tool reaches this capability, so an `exposeAs` here rendered a
    // Settings > Models agent-default row the agent could never honour. Manual
    // use is unaffected: `exposeAs` gates agent surfaces only. Re-add the role in
    // the same change that adds the tool.
    agent: { exposeAs: [] },
    ui: {
      generativeFill: {
        variant: 'precise',
        order: 1,
        tierLabel: 'Balanced',
        label: 'FLUX Pro Fill',
        enhancementKey: 'generativeFill:precise:fluxFill',
        serviceKey: 'inpaint',
        imageInputMode: 'image',
        defaultParams: {
          enhancePrompt: false,
          outputFormat: 'png',
          safetyTolerance: '2',
        },
      },
    },
    verification: imageVerification('fal-ai/flux-pro/v1/fill'),
  },
  {
    id: 'gpt-image-2.5-inpaint',
    label: 'GPT Image 2.5 Flare',
    family: ModelFamily.IMAGE,
    iconKey: 'openai',
    pricing: { unit: 'image', usd: { min: 0.004, max: 0.4 }, asOf: '2026-09-09' },
    qualityTier: 'premium',
    timing: timing(70, { qualities: { low: 0.45, medium: 0.75, high: 1, xhigh: 1.5, max: 2.2 } }),
    surfaces: [ModelSurface.IMAGE_INPAINT],
    topologies: [ModelTopology.MASK_IMAGE_TO_IMAGE],
    features: imageFeatures({ maxOutputImages: 4, maxInputEdit: 1 }),
    references: noReferences,
    routes: {
      [ModelTopology.MASK_IMAGE_TO_IMAGE]: imageRoute({
        endpoint: 'openai/gpt-image-2.5/flare/edit',
        adapterId: 'image.inpaint.mask',
        serviceKey: 'gptImage25Inpaint',
        exportName: 'generate',
        references: noReferences,
        timing: timing(70, { qualities: { low: 0.45, medium: 0.75, high: 1, xhigh: 1.5, max: 2.2 } }),
      }),
    },
    prompt: { enhancerId: 'generativeFill:precise:gptImage25' },
    // No agent tool reaches this capability, so an `exposeAs` here rendered a
    // Settings > Models agent-default row the agent could never honour. Manual
    // use is unaffected: `exposeAs` gates agent surfaces only. Re-add the role in
    // the same change that adds the tool.
    agent: { exposeAs: [] },
    ui: {
      generativeFill: {
        variant: 'precise',
        order: 2,
        tierLabel: 'Most Precise',
        label: 'GPT Image 2.5 Flare',
        enhancementKey: 'generativeFill:precise:gptImage25',
        serviceKey: 'gptImage25Inpaint',
        imageInputMode: 'image',
        defaultParams: {
          imageSize: 'auto',
          outputFormat: 'png',
          quality: 'high',
        },
      },
    },
    verification: imageVerification('openai/gpt-image-2.5/flare/edit'),
  },
  {
    id: 'krea-2-turbo',
    label: 'Krea 2 Turbo',
    series: { id: 'krea-2', version: 'Turbo', rank: 1 },
    family: ModelFamily.IMAGE,
    iconKey: 'krea',
    // fal list price: https://fal.ai/models/fal-ai/krea-2/turbo
    pricing: { unit: 'megapixel', usd: 0.008, asOf: '2026-10-20' },
    qualityTier: 'krea-turbo',
    timing: timing(8, { batchExtraMult: 0.3 }),
    surfaces: [ModelSurface.IMAGE_GENERATE],
    topologies: [ModelTopology.TEXT_TO_IMAGE, ModelTopology.IMAGE_TO_IMAGE],
    features: imageFeatures({ maxOutputImages: 4, maxInputEdit: 3 }),
    references: kreaStyleReferences(3),
    routes: {
      [ModelTopology.TEXT_TO_IMAGE]: imageRoute({
        endpoint: 'fal-ai/krea-2/turbo',
        adapterId: 'image.text.basic',
        serviceKey: 'krea2Turbo',
        exportName: 'generateKrea2Turbo',
        references: noReferences,
        timing: timing(8, { batchExtraMult: 0.3 }),
      }),
      [ModelTopology.IMAGE_TO_IMAGE]: imageRoute({
        endpoint: 'fal-ai/krea-2/turbo/style',
        adapterId: 'image.edit.flat',
        serviceKey: 'krea2Turbo',
        exportName: 'generateKrea2TurboStyle',
        references: kreaStyleReferences(3),
        timing: timing(12, { batchExtraMult: 0.3, inputImageExtraMult: 0.05 }),
      }),
    },
    prompt: { enhancerId: 'krea-2-turbo' },
    agent: { exposeAs: ['image-generator'] },
    description: 'Krea\'s speed-optimized foundation image model, with optional look references.',
    ui: {
      image: {
        editKey: 'krea-2-turbo-style',
        attachedImageMode: 'edit',
        creativeAdapter: 'image-text-basic',
      },
    },
    verification: {
      ...imageVerification('fal-ai/krea-2/turbo'),
      unmappedProviderParams: [
        'acceleration',
        'enable_prompt_expansion',
        'sync_mode',
        'output_format',
        'style_scale',
      ],
    },
  },
  {
    id: 'krea-2-large',
    label: 'Krea 2 Large',
    series: { id: 'krea-2', version: 'Large', rank: 3 },
    family: ModelFamily.IMAGE,
    iconKey: 'krea',
    pricing: { unit: 'image', usd: 0.065, asOf: '2026-08-05' },
    qualityTier: 'krea-large',
    timing: timing(20),
    surfaces: [ModelSurface.IMAGE_GENERATE],
    topologies: [ModelTopology.TEXT_TO_IMAGE, ModelTopology.IMAGE_TO_IMAGE],
    features: {
      ...imageFeatures({ maxOutputImages: 1, maxInputEdit: 10 }),
      // Krea's own dial: how far the model may drift from the literal prompt.
      // Declared here so a later control binds to one contract; until then the
      // service omits the param and the provider default (`medium`) applies.
      creativity: { options: ['raw', 'low', 'medium', 'high'], default: 'medium' },
    },
    references: kreaStyleReferences(10),
    routes: {
      [ModelTopology.TEXT_TO_IMAGE]: imageRoute({
        endpoint: 'krea/v2/large/text-to-image',
        adapterId: 'image.text.basic',
        serviceKey: 'krea2',
        exportName: 'generateKreaV2Large',
        references: noReferences,
        timing: timing(20),
      }),
      [ModelTopology.IMAGE_TO_IMAGE]: imageRoute({
        endpoint: 'krea/v2/large/text-to-image',
        adapterId: 'image.edit.flat',
        serviceKey: 'krea2',
        exportName: 'generateKreaV2LargeStyle',
        references: kreaStyleReferences(10),
        timing: timing(26, { inputImageExtraMult: 0.05 }),
      }),
    },
    prompt: { enhancerId: 'krea-2-large' },
    agent: { exposeAs: ['image-generator'] },
    description: 'Krea\'s flagship foundation image model, built for expressive and unpolished aesthetics.',
    ui: {
      image: {
        editKey: 'krea-2-large-style',
        attachedImageMode: 'edit',
        creativeAdapter: 'image-text-basic',
      },
    },
    verification: {
      ...imageVerification('krea/v2/large/text-to-image'),
      unmappedProviderParams: [
        'styles',
        'moodboards',
        'image_style_references[].strength',
      ],
    },
  },
  {
    id: 'krea-2-medium',
    label: 'Krea 2 Medium',
    series: { id: 'krea-2', version: 'Medium', rank: 2 },
    family: ModelFamily.IMAGE,
    iconKey: 'krea',
    pricing: { unit: 'image', usd: 0.035, asOf: '2026-08-05' },
    qualityTier: 'krea-medium',
    timing: timing(14),
    surfaces: [ModelSurface.IMAGE_GENERATE],
    topologies: [ModelTopology.TEXT_TO_IMAGE, ModelTopology.IMAGE_TO_IMAGE],
    features: {
      ...imageFeatures({ maxOutputImages: 1, maxInputEdit: 10 }),
      creativity: { options: ['raw', 'low', 'medium', 'high'], default: 'medium' },
    },
    references: kreaStyleReferences(10),
    routes: {
      [ModelTopology.TEXT_TO_IMAGE]: imageRoute({
        endpoint: 'krea/v2/medium/text-to-image',
        adapterId: 'image.text.basic',
        serviceKey: 'krea2',
        exportName: 'generateKreaV2Medium',
        references: noReferences,
        timing: timing(14),
      }),
      [ModelTopology.IMAGE_TO_IMAGE]: imageRoute({
        endpoint: 'krea/v2/medium/text-to-image',
        adapterId: 'image.edit.flat',
        serviceKey: 'krea2',
        exportName: 'generateKreaV2MediumStyle',
        references: kreaStyleReferences(10),
        timing: timing(19, { inputImageExtraMult: 0.05 }),
      }),
    },
    prompt: { enhancerId: 'krea-2-medium' },
    agent: { exposeAs: ['image-generator'] },
    activation: { defaultActive: false },
    description: 'Faster, cheaper Krea 2 tuned for illustration, anime, painting, and expressive styles.',
    ui: {
      image: {
        editKey: 'krea-2-medium-style',
        attachedImageMode: 'edit',
        creativeAdapter: 'image-text-basic',
      },
    },
    verification: {
      ...imageVerification('krea/v2/medium/text-to-image'),
      unmappedProviderParams: [
        'styles',
        'moodboards',
        'image_style_references[].strength',
      ],
    },
  },
];

export const IMAGE_MODEL_DEFINITIONS = [
  ...BASE_IMAGE_MODEL_DEFINITIONS,
  // Appended LAST so Midjourney never displaces the existing default/fallback
  // model ordering.
  MIDJOURNEY_MODEL_DEFINITION,
  // Local (GlowGhost Engine) models append after Midjourney for the same
  // reason. They are `defaultActive: false`, so they are absent from every
  // surface until the user turns them on.
  ...GLOWGHOST_ENGINE_MODEL_DEFINITIONS,
];
